import React, { useState, useMemo, useEffect } from 'react';
import {
  X,
  HelpCircle,
  Compass,
  Database,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Scale,
  Building,
  Users,
  Cpu,
  FileText,
  Printer,
  Cloud,
  Download,
  Upload,
  RotateCcw,
  Settings,
  BookOpen,
  Layers,
  Search,
  ExternalLink,
  AlertTriangle,
  Info,
  Lock,
  Shield,
  Zap,
  CheckSquare,
  SlidersHorizontal,
  FolderSync,
  History,
  Workflow
} from 'lucide-react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings?: (tab?: string) => void;
  onOpenSyncModal?: () => void;
  onOpenPrintReport?: () => void;
  onOpenKnowledgeBase?: () => void;
}

type HelpTab = 'workflow' | 'backup-restore' | 'tour' | 'methodology' | 'faq';

export default function HelpModal({
  isOpen,
  onClose,
  onOpenSettings,
  onOpenSyncModal,
  onOpenPrintReport,
  onOpenKnowledgeBase
}: HelpModalProps) {
  if (!isOpen) return null;

  const [activeTab, setActiveTab] = useState<HelpTab>('workflow');
  const [search, setSearch] = useState('');

  // Close on Escape if search is empty, or clear search if filled
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (search) {
          e.stopPropagation();
          setSearch('');
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [search, onClose]);

  // Tab Definitions
  const HELP_TABS = [
    {
      id: 'workflow' as const,
      label: 'Step-by-Step Compliance Roadmap',
      shortTitle: 'Implementation Workflow',
      subtitle: '9-Step Recommended Procedure (Entity Setup to Audit Dossier)',
      icon: Workflow,
      badge: 'Start Here'
    },
    {
      id: 'backup-restore' as const,
      label: 'Backup, Export & Restore Guide',
      shortTitle: 'Backup & Restore',
      subtitle: 'Zero Data Loss, JSON Snapshots & BYOD Cloud Synchronization',
      icon: Database,
      badge: 'Data Safety'
    },
    {
      id: 'tour' as const,
      label: 'Application Feature Tour & Controls',
      shortTitle: 'Feature Tour',
      subtitle: 'Filters, View Modes, Scoping Drawer & Master Data Suite',
      icon: Compass,
      badge: 'UI Tour'
    },
    {
      id: 'methodology' as const,
      label: 'Scoping, Systems & Safe Harbours Methodology',
      shortTitle: 'Methodology & Blueprint',
      subtitle: 'Decision Hierarchy, Systems Mapping Guide & Section 17 Exemptions',
      icon: Scale,
      badge: 'Architecture & Law'
    },
    {
      id: 'faq' as const,
      label: 'Frequently Asked Questions & Privacy',
      shortTitle: 'FAQ & Security',
      subtitle: 'Offline Architecture, Multi-Entity Management & Troubleshooting',
      icon: HelpCircle,
      badge: 'FAQ'
    }
  ];

  // 1. Roadmap Steps Data
  const ROADMAP_STEPS = useMemo(() => [
    {
      id: 'step-1',
      stepNum: 1,
      title: 'Setup Corporate Legal Entity & Governance Profile',
      description: 'Navigate to Settings → Legal Entity Profile & Scope. Enter your company\'s full corporate registration details (Legal Entity Name, CIN, PAN, GSTIN, Incorporation Date, Industry Sector, and Addresses). Designate statutory officers: Data Protection Officer (DPO s.10(2)(a)), Grievance Redressal Officer (s.8(10)), and Corporate Executive Signatory.',
      criteria: [
        'Significant Data Fiduciary (SDF s.10 & r.13): Processing volume, sensitive risk, algorithmic harms.',
        'Children\'s / PwD Data (s.9): Parental consent verification, tracking prohibitions.',
        'Cross-Border Transfers (s.16): Offshore processing countries & SCC safeguards.',
        'Consent Manager Integration (s.6(9)): MeitY-registered provider gateways.'
      ],
      action: { type: 'settings', tab: 'entity', label: 'Open Entity Settings' },
      searchTokens: 'corporate legal entity registration cin pan gstin address dpo grievance signatory sdf children pwh cross border transfer consent manager'
    },
    {
      id: 'step-2',
      stepNum: 2,
      title: 'Register Custom Enterprise Systems Inventory',
      description: 'Every organization operates a distinct IT stack. Navigate to Settings → Architecture Systems to inventory your internal backend microservices, core transaction databases, data lakes, identity providers (IdPs), CRM, HRMS, and third-party SaaS processors. Define technical owners, environments (Cloud / On-Premise), and data sensitivity categories.',
      action: { type: 'settings', tab: 'systems', label: 'Manage Systems' },
      searchTokens: 'register custom enterprise architecture systems inventory software databases analytics identity providers processors data flows mapping'
    },
    {
      id: 'step-3',
      stepNum: 3,
      title: 'Map Architecture Systems to In-Scope Obligations & Rules',
      description: 'Map which registered systems touch personal data flows for each compliance domain. For each operational control (e.g. Consent token verification in API Gateway, SOC breach telemetry, Customer Data Platform for Section 12 erasure workflows), associate the relevant systems so engineering and security teams know exactly where to deploy technical safeguards.',
      bullets: [
        'Identity & Auth (IdP) → Consent capture (OBL-02), Notice delivery (OBL-01), Age gating (OBL-24).',
        'Customer DBs & CDP → Itemised purpose (OBL-03), Erasure upon withdrawal (OBL-19), Access rights (OBL-20).',
        'SOC & Security Telemetry → Immutability (OBL-15), 72-hr DPB/CERT-In breach alert (OBL-16), Security audits (OBL-18).',
        'Tag in Drawer → Open Inspect & Scope on any control and check systems in the "Systems in Scope" selector.'
      ],
      action: { type: 'knowledge', label: 'Systems Blueprint' },
      searchTokens: 'map architecture systems obligations rules data flow identity cdp soc siem erasure notice consent security inspect scope drawer systems in scope'
    },
    {
      id: 'step-4',
      stepNum: 4,
      title: 'Trigger Automated Statutory Scope Rules Engine',
      description: 'Click the ⚡ Auto-Apply Scope Rules button on the Legal Entity screen. The rules engine inspects your corporate scope criteria and automatically evaluates all 54 controls and 102 statutory provisions (e.g. marking SDF controls OBL-33 to OBL-36 and Rule 13 as NOT_APPLICABLE if you are not an SDF, or marking cross-border transfer controls inapplicable if purely domestic).',
      action: { type: 'settings', tab: 'entity', label: '⚡ Auto-Apply Scope' },
      searchTokens: 'rules engine auto apply scope evaluate 54 controls not applicable sdf domestic transfer'
    },
    {
      id: 'step-5',
      stepNum: 5,
      title: 'Record Statutory Exceptions & Safe Harbours (Section 17 & 3(c))',
      description: 'Navigate to Settings → Statutory Exceptions. Record applicable safe harbours (e.g. legal defence under Section 17(1)(a), offence investigation under 17(1)(b), or offshore BPO processing under Section 17(1)(d)). Click ⚡ Auto-Apply Active Safe Harbours to bulk-apply safe harbour exemptions to all mapped controls and statutory rules.',
      action: { type: 'settings', tab: 'exceptions', label: 'Manage Safe Harbours' },
      searchTokens: 'statutory exceptions safe harbours section 17 legal defence court orders bpo offshore bulk apply'
    },
    {
      id: 'step-6',
      stepNum: 6,
      title: 'Directory Management & Control Owner Assignment',
      description: 'In Settings → Compliance Personnel / Users, add your DPO, CISO, Legal Counsel, and engineering leads with designations, emails, phone numbers, and mapped roles. These personnel become available for 1-click assignment in decision sign-offs.',
      action: { type: 'settings', tab: 'users', label: 'Manage Personnel' },
      searchTokens: 'personnel directory users dpo ciso legal counsel engineering lead assignment role owner'
    },
    {
      id: 'step-7',
      stepNum: 7,
      title: 'Conduct Scoping Assessments (Inspect & Scope Drawer)',
      description: 'On the main register, filter by Implementation Tranche, Domain, or System. Click Inspect & Scope on any control or provision to review:',
      bullets: [
        'Record Decision Tab: Toggle status (APPLICABLE, NOT_APPLICABLE, UNRESOLVED), enter legal rationale, select assigned owners, tag systems in scope, and click suggested fact chips.',
        'Assessment Playbook Tab: Answer key evaluation questions and click required entity facts to toggle them directly into your decision notes with real-time checkmark feedback.',
        'Statutory Gazette Tab: Inspect official Gazette notification lines, official PDF links, and SHA-256 verification hashes.'
      ],
      searchTokens: 'inspect scope drawer evaluation status applicable unresolved rationale playbook facts chips gazette hash verification systems in scope'
    },
    {
      id: 'step-8',
      stepNum: 8,
      title: 'Take Backups, Snapshot Exports & Cloud Sync',
      description: 'Click Sync & BYOD in the header. Export a timestamped JSON snapshot for safe offline archival, or connect your organization\'s private REST backend database with API key authentication for collaborative cloud synchronization.',
      action: { type: 'sync', label: 'Sync & BYOD' },
      searchTokens: 'backup snapshot export json sync byod rest api offline archival database'
    },
    {
      id: 'step-9',
      stepNum: 9,
      title: 'Export Formal Attested Executive Audit Workpaper Dossier',
      description: 'Click Audit Report to generate a comprehensive compliance workpaper with corporate identity tables, statutory governance matrices, safe harbours breakdown, system mapping indices, control verification findings, and a formal 3-party attestation sign-off block (DPO, Signatory, Legal Counsel).',
      action: { type: 'print', label: 'Audit Report' },
      searchTokens: 'audit report workpaper dossier printable attestation sign off dpo executive legal'
    }
  ], []);

  // 2. Backup & Portability Sections Data
  const BACKUP_SECTIONS = useMemo(() => [
    {
      id: 'backup-arch',
      type: 'arch',
      title: 'Local-First Zero-Cloud Architecture & Persistence Model',
      description: 'All compliance decisions, company entity details, custom systems, and user profiles are stored locally inside your browser\'s dedicated IndexedDB database (dpdp_offline_workspace_db). No data is sent to external servers unless you explicitly configure a private BYOD REST endpoint.',
      searchTokens: 'local first zero cloud architecture persistence indexeddb offline private database'
    },
    {
      id: 'backup-export',
      type: 'guide',
      title: 'How to Take a Full JSON Backup',
      icon: Download,
      steps: [
        'Click the Sync & BYOD button in the top header.',
        'Under the JSON Snapshot Export section, click "📥 Export Snapshot (.json)".',
        'A timestamped file (e.g. dpdp-compliance-snapshot-2026-09-16.json) will download immediately to your machine.',
        'Store this JSON file in your organization\'s version-controlled repository (Git, SharePoint, S3) for compliance recordkeeping.'
      ],
      note: 'Included in snapshot: All 54 control decisions, 102 statutory rule decisions, custom systems inventory, safe harbour exception records, personnel users, and company profile.',
      searchTokens: 'how to take full json backup export download snapshot git s3 sharepoint'
    },
    {
      id: 'backup-restore',
      type: 'guide',
      title: 'How to Restore from a Backup File',
      icon: Upload,
      steps: [
        'Open the Sync & BYOD modal from the header.',
        'Under Snapshot Import / Restore, click "Choose File" or drag-and-drop your backup JSON file.',
        'The app validates the snapshot structure and displays entity metadata, decisions count, and timestamp.',
        'Click "Confirm & Restore Snapshot" to write the snapshot to IndexedDB and reload your register with zero data loss.'
      ],
      warning: 'Safety Note: Restoring replaces current local decisions with the backup snapshot. Always export a current snapshot before restoring an older file.',
      searchTokens: 'how to restore backup file snapshot import json drag drop validate zero data loss'
    },
    {
      id: 'backup-byod',
      type: 'byod',
      title: 'Bring-Your-Own-Database (BYOD) Private Cloud Sync',
      description: 'If your team operates an enterprise compliance database, internal PostgreSQL/MongoDB service, or private AWS S3/Google Cloud Storage proxy, you can connect it via Sync & BYOD → REST Endpoint Configuration.',
      params: [
        { label: 'Endpoint URL', value: 'https://api.internal/dpdp/sync' },
        { label: 'Bearer Token', value: 'Authorization: Bearer ***' },
        { label: 'Push / Pull Actions', value: '2-Way Sync & Conflict Resolution' }
      ],
      searchTokens: 'byod bring your own database private cloud sync rest endpoint postgresql mongodb s3 gcs bearer token'
    },
    {
      id: 'backup-reset',
      type: 'reset',
      title: 'Factory Baseline Reset',
      description: 'If you ever need to purge local modifications and restart from a pristine statutory state, open Settings → Factory Reset. This resets all decisions and master data to the official September 2026 Gazette baseline.',
      searchTokens: 'factory baseline reset purge clean pristine gazette baseline default master data'
    }
  ], []);

  // 3. UI Tour Sections Data
  const TOUR_SECTIONS = useMemo(() => [
    {
      id: 'tour-viewmodes',
      title: 'Dual View Modes',
      icon: Layers,
      description: 'Switch seamlessly between engineered controls and legal text:',
      bullets: [
        '54 Operational Controls: Engineered controls (OBL-01 to OBL-54) for technical architectures, DPIAs, and operational policies.',
        '102 Statutory Provisions & Rules: Legal clauses directly citation-grounded in the DPDP Act 2023 and DPDP Rules 2025.'
      ],
      searchTokens: 'dual view modes operational controls statutory provisions rules dpdp act 2023 rules 2025'
    },
    {
      id: 'tour-filters',
      title: 'Multi-Dimensional Filter Bar',
      icon: SlidersHorizontal,
      description: 'Filter 156 statutory items across 7 orthogonal dimensions:',
      tags: ['Actor', 'Tranche Timeline', 'Governance Domain', 'System', 'Status', 'Owner', 'Text Search'],
      searchTokens: 'multi dimensional filter bar actor tranche domain system status owner search'
    },
    {
      id: 'tour-drawer',
      title: 'Inspect & Scope Evaluation Drawer',
      icon: FileText,
      description: 'Deep evaluation slide-over popup with 4 specialized tabs:',
      bullets: [
        'Record Decision: Status, notes, rationale, personnel assignment.',
        'Assessment Playbook: Questions & clickable fact checklist with instant notes toggle.',
        'Statutory Gazette: Official passage, line numbers, PDF link, SHA-256 verification hash.',
        'Control Architecture: System mappings & verification test specifications.'
      ],
      searchTokens: 'inspect scope drawer evaluation record decision assessment playbook gazette passage architecture systems'
    },
    {
      id: 'tour-settings',
      title: 'Master Data & Governance Suite',
      icon: Settings,
      description: 'Vertical sidebar featuring 12 configuration tabs for full enterprise customization of company profiles, personnel directory, custom systems, and safe harbour rules.',
      searchTokens: 'master data settings governance suite company profile directory taxonomy custom systems exceptions reset'
    }
  ], []);

  // 4. Methodology Sections Data
  const METHODOLOGY_SECTIONS = useMemo(() => [
    {
      id: 'methodology-system-mapping',
      title: 'How to Map Enterprise Architecture Systems to DPDP Obligations',
      type: 'system-mapping' as const,
      statutoryGround: 'DPDP Act Section 8(5) & Section 12; DPDP Rules Rule 6',
      description: 'Under DPDP Act s.8(5), Data Fiduciaries must implement reasonable technical and organizational security safeguards across all internal and cloud infrastructure processing personal data. Systematic systems-to-obligations mapping ensures audit traceability, instant root-cause isolation during breach reporting (Rule 6), and automated execution of Data Principal erasure workflows (s.12).',
      steps: [
        {
          num: 1,
          title: 'Register Custom Systems in Master Data',
          desc: 'Go to Settings → Architecture Systems. Add your enterprise applications, databases, identity providers, and third-party SaaS vendors.'
        },
        {
          num: 2,
          title: 'Identify Ingestion & Processing Touchpoints',
          desc: 'Determine which systems ingest personal data (web forms, mobile SDKs, public APIs) and which backend stores persist or process that data.'
        },
        {
          num: 3,
          title: 'Map Systems by Core Operational Domain',
          desc: 'Link each system to relevant obligation domains (Notice/Consent, Security Safeguards, Principal Rights/Erasure, Breach Escalation, TPRM).'
        },
        {
          num: 4,
          title: 'Tag Systems in Scoping Drawer',
          desc: 'Open Inspect & Scope on each obligation and select the mapped systems in the "Systems in Scope" selector to record formal technical ownership.'
        },
        {
          num: 5,
          title: 'Audit & Filter via Dashboard',
          desc: 'Use the System Filter on the main dashboard to verify that all registered systems have 100% evaluated controls and zero unassessed gaps.'
        }
      ],
      systemProfiles: [
        {
          system: 'Identity Provider / Auth Service (e.g. Keycloak / Okta)',
          type: 'Auth & Onboarding',
          obligations: ['OBL-01 (Notice)', 'OBL-02 (Consent Capture)', 'OBL-24 (Age Gating & Verification)'],
          justification: 'Primary gateway where Data Principals are authenticated, notices are rendered, and initial consent tokens are minted.'
        },
        {
          system: 'Customer Data Platform (CDP) / Master DB',
          type: 'Core Data Store',
          obligations: ['OBL-03 (Purpose Limitation)', 'OBL-19 (Automated Erasure)', 'OBL-20 (Principal Access Rights)'],
          justification: 'Stores core personal data records; must execute erasure cascades and export machine-readable summaries upon request.'
        },
        {
          system: 'SOC / SIEM / Centralized Log Vault',
          type: 'Security & Telemetry',
          obligations: ['OBL-15 (Log Immutability)', 'OBL-16 (72-hr DPB/CERT-In Breach Escalation)', 'OBL-18 (Vulnerability Audit)'],
          justification: 'Maintains tamper-proof access trails and monitors unauthorized access for mandatory breach reporting.'
        },
        {
          system: 'Payment Gateway / Financial Microservice',
          type: 'Transaction Processing',
          obligations: ['OBL-05 (Legitimate Uses s.7)', 'OBL-22 (Cross-Border Financial Telemetry)'],
          justification: 'Processes billing tokens and bank coordinates under legitimate financial use; enforces cross-border transfer controls.'
        },
        {
          system: 'HRMS / Employee Management Suite',
          type: 'Internal HR',
          obligations: ['OBL-07 (Employment Legitimate Uses s.7(i))', 'OBL-14 (Role-Based Access Control)'],
          justification: 'Processes employee personal data; exempt from explicit consent under s.7(i) but requires strict internal access segregation.'
        }
      ],
      searchTokens: 'how to map systems to obligations architecture systems inventory data flow idp cdp soc siem hrms payment gateway technical safeguards s.8(5) erasure s.12 breach rule 6'
    },
    {
      id: 'methodology-hierarchy',
      title: 'Applicability Decision Hierarchy & Classification Standards',
      type: 'hierarchy' as const,
      statuses: [
        {
          name: 'APPLICABLE',
          icon: CheckCircle2,
          colorClass: 'emerald',
          description: 'Your enterprise performs processing triggering this statutory provision. Technical and procedural controls must be implemented and evidenced.'
        },
        {
          name: 'NOT APPLICABLE',
          icon: X,
          colorClass: 'slate',
          description: 'Activity is legally exempt under Section 17 safe harbours, Section 3 exclusions, or out of scope (e.g. Non-SDF, purely domestic, no child data).'
        },
        {
          name: 'UNRESOLVED',
          icon: AlertTriangle,
          colorClass: 'amber',
          description: 'Potential applicability identified, but facts require engineering verification or outside legal counsel sign-off before closing.'
        }
      ],
      searchTokens: 'decision hierarchy classification standards applicable not applicable unresolved status criteria'
    },
    {
      id: 'methodology-safeharbours',
      title: 'Section 17 Statutory Safe Harbours Summary',
      type: 'safeharbours' as const,
      harbours: [
        { citation: 's.17(1)(a)', title: 'Legal Claims & Court Proceedings', description: 'Exempt from notice and consent when enforcing legal rights or defending claims.' },
        { citation: 's.17(1)(b)', title: 'Offence Investigation & Prevention', description: 'Exempt for internal fraud detection, law enforcement coordination, and cybersecurity incident response.' },
        { citation: 's.17(1)(d)', title: 'Offshore BPO / IT Contracts', description: 'Processing non-Indian principal data under contract with foreign entity is exempt from Chapters II-IV.' },
        { citation: 's.3(c)(ii)', title: 'Publicly Disclosed Personal Data', description: 'Data voluntarily made publicly available by the principal is exempt from DPDP consent rules.' }
      ],
      searchTokens: 'section 17 statutory safe harbours legal claims court offence investigation fraud bpo it contracts public data'
    }
  ], []);

  // 5. FAQ Items Data
  const FAQ_ITEMS = useMemo(() => [
    {
      id: 'faq-security',
      question: 'Is our compliance and corporate data secure and private?',
      icon: Lock,
      answer: 'Yes, completely. This application is an offline-first Progressive Web Application (PWA). All evaluation decisions, rationale notes, employee personnel lists, and systems inventory are stored exclusively inside your browser\'s private IndexedDB storage. Zero data is transmitted to external telemetry or third-party servers.',
      searchTokens: 'security privacy confidential data safe offline private indexeddb telemetry zero egress'
    },
    {
      id: 'faq-multi-entity',
      question: 'How do I manage compliance for multiple subsidiary legal entities?',
      icon: Building,
      answer: 'You can create an assessment snapshot for your primary entity, export the JSON snapshot (e.g. entity-retail.json), update the entity profile for your subsidiary, and export a separate snapshot (e.g. entity-fintech.json). Use Sync & BYOD → Snapshot Import to instantly switch workspaces between subsidiaries.',
      searchTokens: 'multiple subsidiaries entities group companies workspace snapshot import export switch'
    },
    {
      id: 'faq-statutory-updates',
      question: 'What happens if the Government of India updates DPDP statutory rules?',
      icon: History,
      answer: 'You can customize any statutory provision or operational control directly in Settings → Statutory Provisions / Controls without losing your existing applicability decisions, or import an updated master data package.',
      searchTokens: 'government india rules update amendments customize settings statutory provisions master data'
    },
    {
      id: 'faq-shortcuts',
      question: 'Keyboard Shortcuts & Quick Navigation',
      icon: Zap,
      answer: 'Use these keyboard shortcuts to navigate rapidly anywhere in the application:',
      shortcuts: [
        { key: '? / Shift+/', desc: 'Open Help Guide' },
        { key: 'Esc', desc: 'Close Modals / Clear Search' },
        { key: 'Ctrl + P', desc: 'Print Dossier' },
        { key: 'Click Tag', desc: 'Filter View' }
      ],
      searchTokens: 'keyboard shortcuts hotkeys quick navigation esc question mark print filter'
    }
  ], []);

  // Filter Query Helper
  const q = search.toLowerCase().trim();

  // Filter Tab Items
  const filteredRoadmap = useMemo(() => {
    if (!q) return ROADMAP_STEPS;
    return ROADMAP_STEPS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.searchTokens.toLowerCase().includes(q) ||
        s.criteria?.some((c) => c.toLowerCase().includes(q)) ||
        s.bullets?.some((b) => b.toLowerCase().includes(q))
    );
  }, [ROADMAP_STEPS, q]);

  const filteredBackup = useMemo(() => {
    if (!q) return BACKUP_SECTIONS;
    return BACKUP_SECTIONS.filter(
      (b) =>
        b.title.toLowerCase().includes(q) ||
        b.description?.toLowerCase().includes(q) ||
        b.searchTokens.toLowerCase().includes(q) ||
        b.steps?.some((st) => st.toLowerCase().includes(q)) ||
        b.note?.toLowerCase().includes(q) ||
        b.warning?.toLowerCase().includes(q)
    );
  }, [BACKUP_SECTIONS, q]);

  const filteredTour = useMemo(() => {
    if (!q) return TOUR_SECTIONS;
    return TOUR_SECTIONS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.searchTokens.toLowerCase().includes(q) ||
        t.bullets?.some((b) => b.toLowerCase().includes(q)) ||
        t.tags?.some((tg) => tg.toLowerCase().includes(q))
    );
  }, [TOUR_SECTIONS, q]);

  const filteredMethodology = useMemo(() => {
    if (!q) return METHODOLOGY_SECTIONS;
    return METHODOLOGY_SECTIONS.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.searchTokens.toLowerCase().includes(q) ||
        m.statuses?.some((st) => st.name.toLowerCase().includes(q) || st.description.toLowerCase().includes(q)) ||
        m.harbours?.some((h) => h.citation.toLowerCase().includes(q) || h.title.toLowerCase().includes(q) || h.description.toLowerCase().includes(q))
    );
  }, [METHODOLOGY_SECTIONS, q]);

  const filteredFaq = useMemo(() => {
    if (!q) return FAQ_ITEMS;
    return FAQ_ITEMS.filter(
      (f) =>
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q) ||
        f.searchTokens.toLowerCase().includes(q) ||
        f.shortcuts?.some((sc) => sc.key.toLowerCase().includes(q) || sc.desc.toLowerCase().includes(q))
    );
  }, [FAQ_ITEMS, q]);

  // Tab Match Counts
  const tabCounts: Record<HelpTab, number> = useMemo(() => ({
    workflow: filteredRoadmap.length,
    'backup-restore': filteredBackup.length,
    tour: filteredTour.length,
    methodology: filteredMethodology.length,
    faq: filteredFaq.length
  }), [filteredRoadmap, filteredBackup, filteredTour, filteredMethodology, filteredFaq]);

  const totalMatches = tabCounts.workflow + tabCounts['backup-restore'] + tabCounts.tour + tabCounts.methodology + tabCounts.faq;
  const currentTabInfo = HELP_TABS.find((t) => t.id === activeTab) || HELP_TABS[0];
  const currentCount = tabCounts[activeTab];

  const handleActionClick = (action: { type: string; tab?: string; modal?: string }) => {
    onClose();
    if (action.type === 'settings' && onOpenSettings) {
      onOpenSettings(action.tab);
    } else if (action.type === 'sync' && onOpenSyncModal) {
      onOpenSyncModal();
    } else if (action.type === 'print' && onOpenPrintReport) {
      onOpenPrintReport();
    } else if (action.type === 'knowledge' && onOpenKnowledgeBase) {
      onOpenKnowledgeBase();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-slate-950/75 backdrop-blur-xs">
      <div className="relative w-full max-w-5xl xl:max-w-6xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col h-[90vh] max-h-[90vh]">
        
        {/* Header */}
        <div className="px-6 py-4.5 sm:px-8 sm:py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/80 dark:bg-slate-900/80 shrink-0">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <HelpCircle className="w-5 h-5" />
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight">
                  DPDP Obligations Register & Decision Workspace Guide
                </h3>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-accent/10 text-accent dark:text-accent-light border border-accent/20">
                  User Handbook
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 hidden sm:block">
                Comprehensive onboarding, step-by-step roadmap, offline backup/restore tutorials, and statutory methodology
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl inline-flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            title="Close Guide (Esc)"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* Modal Body: Left Vertical Icon Rail + Right Content Area */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          
          {/* Vertical Tabs: Icon Only Triggers + Tooltips on Hover */}
          <aside className="w-16 sm:w-20 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-950/60 py-5 flex flex-col items-center gap-3.5 overflow-y-auto z-20">
            {HELP_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const matchesInTab = tabCounts[tab.id];
              return (
                <div key={tab.id} className="relative group">
                  <button
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-all cursor-pointer relative ${
                      isActive
                        ? 'bg-accent text-white shadow-lg shadow-accent/30 ring-2 ring-accent/30'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/80 dark:hover:bg-slate-800/90'
                    }`}
                    aria-label={tab.label}
                  >
                    <tab.icon className="w-5 h-5" />
                    
                    {/* Active indicator bar */}
                    {isActive && (
                      <span className="absolute -left-2.5 sm:-left-3.5 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-accent rounded-r-full" />
                    )}

                    {/* Search match badge */}
                    {q && matchesInTab > 0 && !isActive && (
                      <span className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center shadow-xs">
                        {matchesInTab}
                      </span>
                    )}
                  </button>

                  {/* Floating Tooltip with Text on Hover */}
                  <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3.5 z-50 hidden group-hover:flex items-center">
                    <div className="bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold px-3.5 py-2.5 rounded-xl shadow-2xl whitespace-nowrap border border-slate-700/80 flex items-center gap-3 animate-in fade-in zoom-in-95 duration-100">
                      <div className="flex flex-col text-left space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-100">{tab.label}</span>
                          {q && (
                            <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono font-bold ${matchesInTab > 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
                              {matchesInTab} {matchesInTab === 1 ? 'match' : 'matches'}
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-slate-400 font-normal">{tab.subtitle}</span>
                      </div>
                      {tab.badge && !q && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-accent/20 text-accent-light border border-accent/30">
                          {tab.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </aside>

          {/* Right Main Content Pane */}
          <main className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-900 overflow-hidden">
            
            {/* Active Tab Toolbar & Search Bar */}
            <div className="px-6 py-4 sm:px-8 sm:py-4.5 border-b border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-accent/10 dark:bg-accent/20 text-accent flex items-center justify-center shrink-0">
                  <currentTabInfo.icon className="w-4.5 h-4.5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100">
                      {currentTabInfo.label}
                    </h4>
                    {q && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-mono font-bold bg-accent/10 text-accent dark:text-accent-light border border-accent/20">
                        {currentCount} {currentCount === 1 ? 'result' : 'results'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {currentTabInfo.subtitle}
                  </p>
                </div>
              </div>

              {/* Search Filter Box */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter guide content..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-8 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent w-full sm:w-64 shadow-xs"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer p-0.5 rounded"
                    title="Clear Filter"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Content Body */}
            <div className="p-6 sm:p-8 overflow-y-auto space-y-6 sm:space-y-7 flex-1 text-slate-700 dark:text-slate-300">
              
              {/* Cross-Tab Search Suggestions Banner */}
              {q && currentCount === 0 && totalMatches > 0 && (
                <div className="p-4.5 rounded-2xl bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 space-y-2.5 animate-in fade-in duration-150">
                  <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-bold text-xs sm:text-sm">
                    <Info className="w-4 h-4 text-amber-600" />
                    <span>No direct results in "{currentTabInfo.shortTitle}"</span>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    We found <strong>{totalMatches} matching items</strong> in other sections of the guide. Click below to view:
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {HELP_TABS.map((t) => {
                      const c = tabCounts[t.id];
                      if (c === 0 || t.id === activeTab) return null;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setActiveTab(t.id)}
                          className="h-8 px-3 rounded-xl text-xs font-bold bg-amber-200/60 dark:bg-amber-900/50 hover:bg-amber-200 dark:hover:bg-amber-900 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-800 inline-flex items-center gap-1.5 cursor-pointer transition-colors"
                        >
                          <t.icon className="w-3.5 h-3.5" />
                          <span>{t.shortTitle}</span>
                          <span className="px-1.5 py-0.2 rounded-full bg-amber-300/60 dark:bg-amber-800 text-[10px] font-mono font-extrabold">
                            {c}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty State when no results found anywhere */}
              {q && totalMatches === 0 && (
                <div className="py-12 flex flex-col items-center justify-center text-center space-y-3.5">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center">
                    <Search className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h5 className="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-200">
                      No results found for "{search}"
                    </h5>
                    <p className="text-xs text-slate-500 max-w-sm">
                      Try searching with different terms like "SDF", "Backup", "Safe Harbour", "Drawer", or "Restore".
                    </p>
                  </div>
                  <button
                    onClick={() => setSearch('')}
                    className="h-8 px-4 rounded-xl text-xs font-bold text-accent dark:text-accent-light bg-accent/10 hover:bg-accent/20 border border-accent/20 transition-colors cursor-pointer"
                  >
                    Clear Filter
                  </button>
                </div>
              )}

              {/* ========================================================================= */}
              {/* TAB 1: STEP-BY-STEP COMPLIANCE ROADMAP */}
              {/* ========================================================================= */}
              {activeTab === 'workflow' && (
                <div className="space-y-6 sm:space-y-7">
                  
                  {/* Hero Intro (hidden if searching) */}
                  {!q && (
                    <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-accent/10 via-indigo-50/40 to-transparent dark:from-accent/15 dark:via-indigo-950/20 border border-accent/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4.5 h-4.5 text-accent" />
                          <span className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
                            Recommended End-to-End Implementation Methodology
                          </span>
                        </div>
                        <p className="text-xs sm:text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
                          Follow this 8-step sequence to configure enterprise entity governance, evaluate applicability, map architecture systems, and export formal attested audit dossiers.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 8-Step Timeline Cards */}
                  <div className="space-y-4 sm:space-y-5">
                    {filteredRoadmap.map((step) => (
                      <div
                        key={step.id}
                        className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3.5"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className={`w-7 h-7 rounded-full text-white font-mono font-bold text-xs flex items-center justify-center shrink-0 ${step.stepNum === 9 ? 'bg-emerald-600' : 'bg-accent'}`}>
                              {step.stepNum}
                            </span>
                            <h5 className="font-bold text-sm sm:text-base text-slate-900 dark:text-slate-100">
                              {step.title}
                            </h5>
                          </div>
                          {step.action && (
                            <button
                              type="button"
                              onClick={() => handleActionClick(step.action!)}
                              className={`h-8 px-3 rounded-xl text-xs font-bold transition-colors self-start sm:self-auto cursor-pointer inline-flex items-center gap-1.5 shrink-0 ${
                                step.action.type === 'sync'
                                  ? 'text-white bg-accent hover:bg-accent-dark shadow-xs'
                                  : step.action.type === 'print'
                                  ? 'text-slate-800 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600'
                                  : step.action.type === 'knowledge'
                                  ? 'text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/80 border border-indigo-200 dark:border-indigo-800'
                                  : 'text-accent dark:text-accent-light bg-accent/10 hover:bg-accent/20 border border-accent/20'
                              }`}
                            >
                              {step.action.type === 'sync' && <Cloud className="w-3.5 h-3.5" />}
                              {step.action.type === 'print' && <Printer className="w-3.5 h-3.5" />}
                              {step.action.type === 'knowledge' && <BookOpen className="w-3.5 h-3.5" />}
                              <span>{step.action.label}</span>
                              {step.action.type === 'settings' && <ArrowRight className="w-3.5 h-3.5" />}
                              {step.action.type === 'knowledge' && <ExternalLink className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                        <p className="text-xs sm:text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">
                          {step.description}
                        </p>

                        {/* Criteria list if present */}
                        {step.criteria && (
                          <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 text-xs space-y-2">
                            <span className="font-bold text-slate-800 dark:text-slate-200 block">Key Statutory Scope Criteria to Toggle:</span>
                            <ul className="list-disc pl-4.5 space-y-1 text-slate-600 dark:text-slate-400">
                              {step.criteria.map((c, i) => (
                                <li key={i}>{c}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Bullets if present */}
                        {step.bullets && (
                          <ul className="list-disc pl-5 text-xs sm:text-[13px] space-y-1.5 text-slate-600 dark:text-slate-400">
                            {step.bullets.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>

                </div>
              )}

              {/* ========================================================================= */}
              {/* TAB 2: BACKUP, EXPORT & RESTORE GUIDE */}
              {/* ========================================================================= */}
              {activeTab === 'backup-restore' && (
                <div className="space-y-6 sm:space-y-7">
                  {filteredBackup.map((sec) => {
                    if (sec.type === 'arch') {
                      return (
                        <div key={sec.id} className="p-5 sm:p-6 rounded-2xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/50 space-y-2.5">
                          <div className="flex items-center gap-2.5">
                            <ShieldCheck className="w-5 h-5 text-accent" />
                            <h5 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
                              {sec.title}
                            </h5>
                          </div>
                          <p className="text-xs sm:text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">
                            {sec.description}
                          </p>
                        </div>
                      );
                    }

                    if (sec.type === 'guide') {
                      const isRestore = sec.id === 'backup-restore';
                      return (
                        <div key={sec.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-4">
                          <div className="flex items-center gap-2.5 text-slate-900 dark:text-slate-100">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isRestore ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-accent/15 text-accent'}`}>
                              {sec.icon && <sec.icon className="w-4 h-4" />}
                            </div>
                            <h5 className="font-bold text-xs sm:text-sm">{sec.title}</h5>
                          </div>
                          {sec.steps && (
                            <ol className="list-decimal pl-4.5 space-y-2 text-xs sm:text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">
                              {sec.steps.map((st, i) => (
                                <li key={i}>{st}</li>
                              ))}
                            </ol>
                          )}
                          {sec.note && (
                            <div className="p-3.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 text-xs text-slate-500 space-y-1">
                              <strong className="text-slate-700 dark:text-slate-300 block">{sec.note}</strong>
                            </div>
                          )}
                          {sec.warning && (
                            <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 text-xs text-amber-800 dark:text-amber-300 space-y-1">
                              <p className="font-medium">{sec.warning}</p>
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (sec.type === 'byod') {
                      return (
                        <div key={sec.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3.5">
                          <div className="flex items-center gap-2.5">
                            <FolderSync className="w-5 h-5 text-accent" />
                            <h5 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100">
                              {sec.title}
                            </h5>
                          </div>
                          <p className="text-xs sm:text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">
                            {sec.description}
                          </p>
                          {sec.params && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-1.5 text-xs">
                              {sec.params.map((p, i) => (
                                <div key={i} className="p-3.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-1">
                                  <strong className="block text-slate-800 dark:text-slate-200">{p.label}</strong>
                                  <span className="text-[11px] text-slate-500 font-mono break-all">{p.value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (sec.type === 'reset') {
                      return (
                        <div key={sec.id} className="p-4.5 sm:p-5 rounded-2xl bg-rose-50/60 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 space-y-2">
                          <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
                            <RotateCcw className="w-4.5 h-4.5" />
                            <h5 className="font-bold text-xs sm:text-sm">{sec.title}</h5>
                          </div>
                          <p className="text-xs sm:text-[13px] text-rose-600 dark:text-rose-300 leading-relaxed">
                            {sec.description}
                          </p>
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              )}

              {/* ========================================================================= */}
              {/* TAB 3: APPLICATION FEATURE TOUR */}
              {/* ========================================================================= */}
              {activeTab === 'tour' && (
                <div className="space-y-5 sm:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                    {filteredTour.map((item) => (
                      <div key={item.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3">
                        <div className="flex items-center gap-2.5">
                          <item.icon className="w-4.5 h-4.5 text-accent" />
                          <h5 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100">{item.title}</h5>
                        </div>
                        <p className="text-xs sm:text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed">
                          {item.description}
                        </p>
                        {item.bullets && (
                          <ul className="list-disc pl-4.5 text-xs sm:text-[13px] space-y-1.5 text-slate-600 dark:text-slate-400">
                            {item.bullets.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        )}
                        {item.tags && (
                          <div className="flex flex-wrap gap-1.5 text-xs pt-1">
                            {item.tags.map((t, i) => (
                              <span key={i} className="px-2.5 py-1 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 font-medium">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ========================================================================= */}
              {/* TAB 4: LEGAL METHODOLOGY & SAFE HARBOURS */}
              {/* ========================================================================= */}
              {activeTab === 'methodology' && (
                <div className="space-y-5 sm:space-y-6">
                  {filteredMethodology.map((sec) => {
                    if (sec.type === 'system-mapping') {
                      return (
                        <div key={sec.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 dark:border-slate-700/80 pb-3.5">
                            <div className="space-y-1">
                              <h5 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                <Cpu className="w-4.5 h-4.5 text-indigo-600 dark:text-indigo-400" />
                                <span>{sec.title}</span>
                              </h5>
                              {sec.statutoryGround && (
                                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                  {sec.statutoryGround}
                                </span>
                              )}
                            </div>
                            {onOpenSettings && (
                              <button
                                type="button"
                                onClick={() => {
                                  onClose();
                                  onOpenSettings('systems');
                                }}
                                className="h-8 px-3 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1.5 cursor-pointer shadow-xs transition-colors self-start sm:self-auto"
                              >
                                <Settings className="w-3.5 h-3.5" />
                                <span>Manage Systems</span>
                              </button>
                            )}
                          </div>

                          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            {sec.description}
                          </p>

                          {/* 5-Step Architecture Flow */}
                          <div className="space-y-2 pt-1">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              Recommended 5-Step Systems Mapping Procedure:
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2.5">
                              {sec.steps?.map((step: any) => (
                                <div key={step.num} className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-1">
                                  <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 font-mono font-bold text-[10px] flex items-center justify-center">
                                    {step.num}
                                  </div>
                                  <h6 className="font-bold text-[11px] text-slate-900 dark:text-slate-100 leading-tight">
                                    {step.title}
                                  </h6>
                                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-normal">
                                    {step.desc}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Enterprise Reference Mappings */}
                          <div className="space-y-2 pt-2 border-t border-slate-200/80 dark:border-slate-700/80">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              Standard Enterprise System Archetypes & DPDP Obligation Mappings:
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                              {sec.systemProfiles?.map((sp: any, i: number) => (
                                <div key={i} className="p-3.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-bold text-slate-900 dark:text-slate-100 text-xs">
                                      {sp.system}
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                      {sp.type}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {sp.obligations?.map((obl: string, oi: number) => (
                                      <span key={oi} className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/60">
                                        {obl}
                                      </span>
                                    ))}
                                  </div>
                                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                    {sp.justification}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (sec.type === 'hierarchy') {
                      return (
                        <div key={sec.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-4">
                          <h5 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100">
                            {sec.title}
                          </h5>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                            {sec.statuses?.map((st, i) => (
                              <div
                                key={i}
                                className={`p-4 rounded-xl space-y-2 border ${
                                  st.colorClass === 'emerald'
                                    ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/60'
                                    : st.colorClass === 'amber'
                                    ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/60'
                                    : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                                }`}
                              >
                                <div className={`font-bold flex items-center gap-1.5 text-xs sm:text-[13px] ${
                                  st.colorClass === 'emerald'
                                    ? 'text-emerald-800 dark:text-emerald-300'
                                    : st.colorClass === 'amber'
                                    ? 'text-amber-800 dark:text-amber-300'
                                    : 'text-slate-800 dark:text-slate-200'
                                }`}>
                                  <st.icon className="w-4 h-4" />
                                  <span>{st.name}</span>
                                </div>
                                <p className={`text-xs leading-relaxed ${
                                  st.colorClass === 'emerald'
                                    ? 'text-emerald-700 dark:text-emerald-400'
                                    : st.colorClass === 'amber'
                                    ? 'text-amber-700 dark:text-amber-400'
                                    : 'text-slate-600 dark:text-slate-400'
                                }`}>
                                  {st.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    if (sec.type === 'safeharbours') {
                      return (
                        <div key={sec.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                              <Scale className="w-4.5 h-4.5 text-accent" />
                              <span>{sec.title}</span>
                            </div>
                            {onOpenKnowledgeBase && (
                              <button
                                type="button"
                                onClick={() => {
                                  onClose();
                                  onOpenKnowledgeBase();
                                }}
                                className="text-xs text-accent hover:underline flex items-center gap-1 cursor-pointer font-semibold"
                              >
                                <span>Full Knowledge Base</span>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
                            {sec.harbours?.map((h, i) => (
                              <div key={i} className="p-3.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-1">
                                <span className="font-bold text-slate-800 dark:text-slate-200 block">
                                  {h.citation} — {h.title}
                                </span>
                                <p className="text-xs text-slate-500 leading-relaxed">{h.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              )}

              {/* ========================================================================= */}
              {/* TAB 5: FAQ & TROUBLESHOOTING */}
              {/* ========================================================================= */}
              {activeTab === 'faq' && (
                <div className="space-y-4 sm:space-y-5">
                  {filteredFaq.map((item) => (
                    <div key={item.id} className="p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-2">
                      <h5 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <item.icon className="w-4.5 h-4.5 text-accent" />
                        <span>{item.question}</span>
                      </h5>
                      <p className="text-xs sm:text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed">
                        {item.answer}
                      </p>
                      {item.shortcuts && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 pt-1 text-xs font-mono text-slate-600 dark:text-slate-400">
                          {item.shortcuts.map((sc, i) => (
                            <div key={i} className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 space-y-0.5">
                              <span className="font-bold text-accent block">{sc.key}</span>
                              <span className="text-[11px]">{sc.desc}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          </main>

        </div>

      </div>
    </div>
  );
}